import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { auditService } from '../services/auditService';
import { AuditListParams, AuditLogEntry } from '../types/audit';
import AuditLogTable from '../components/audit/AuditLogTable';
import AuditDashboard from '../components/audit/AuditDashboard';
import { isAbortError } from '../utils/abortError';
import { useAuditUrlState } from '../hooks/useAuditUrlState';
import '../styles/config-templates.css';
import '../styles/dashboard.css';

const AUTO_REFRESH_INTERVAL_MS = 30_000;

// A CSV filename that tells the export apart later, e.g.
// `audit-log-failed-actor-alice-2026-05-05.csv`. File-system-safe only.
function buildExportFilename(filters: AuditListParams): string {
  const parts = ['audit-log'];
  if (filters.status) parts.push(filters.status);
  if (filters.resource_type) parts.push(filters.resource_type);
  if (filters.actor_username) parts.push('actor-' + filters.actor_username);
  if (filters.action) parts.push(filters.action);
  parts.push(new Date().toISOString().slice(0, 10));
  return (
    parts
      .map((p) => p.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, ''))
      .filter(Boolean)
      .join('-') + '.csv'
  );
}

/**
 * The audit log: a dashboard of who did what over a period, and the
 * event list with its filters. Filters, page and tab live in the URL.
 */
export const AuditLogPage: React.FC = () => {
  const { canViewAuditLog } = useClientAccess();
  const { t, formatTime } = useI18n();

  const {
    state: { tab: activeTab, filters, page, pageSize },
    setTab,
    setFilters,
    setPage,
    setPageSize,
    patch,
  } = useAuditUrlState();

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  // The in-flight load, so cleanup and refresh can abort it.
  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!canViewAuditLog) return;
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.listAuditLog(
        { ...filters, page, page_size: pageSize },
        { signal },
      );
      setItems(response.items || []);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[AUDIT LOG PAGE] Failed to fetch audit entries:', err);
      setError(err instanceof Error ? err.message : t('audit.loading'));
      setItems([]);
      setTotal(0);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [canViewAuditLog, filters, page, pageSize, t]);

  useEffect(() => {
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // Auto-refresh: poll the list every 30 s while the toggle is on and the
  // document is visible, so background tabs do not hammer the API.
  useEffect(() => {
    if (!autoRefresh || activeTab !== 'list') return;
    let intervalId: number | undefined;
    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };
    const start = () => {
      stop();
      intervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          loadAbortRef.current?.abort();
          const ac = new AbortController();
          loadAbortRef.current = ac;
          load(ac.signal);
        }
      }, AUTO_REFRESH_INTERVAL_MS);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [autoRefresh, activeTab, load]);

  const handleRefresh = useCallback(() => {
    loadAbortRef.current?.abort();
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
  }, [load]);

  // Drill-down from the dashboard: merge the filters, page 1, Events tab.
  const handleDrillDown = useCallback(
    (next: Partial<AuditListParams>) => {
      patch({
        filters: { ...filters, ...next },
        page: 1,
        tab: 'list',
      });
    },
    [filters, patch],
  );

  const handleExportCsv = useCallback(async () => {
    if (!canViewAuditLog) return;
    setLoading(true);
    setError(null);
    try {
      const EXPORT_PAGE_SIZE = 500;
      const MAX_PAGES = 20; // 10k rows max per export
      const MAX_CONCURRENCY = 5;

      const first = await auditService.listAuditLog({ ...filters, page: 1, page_size: EXPORT_PAGE_SIZE });
      const rows: AuditLogEntry[] = [...(first.items || [])];
      const totalPages = Math.min(MAX_PAGES, Math.ceil((first.total || rows.length) / EXPORT_PAGE_SIZE));

      const pageNumbers: number[] = [];
      for (let p = 2; p <= totalPages; p++) pageNumbers.push(p);

      for (let i = 0; i < pageNumbers.length; i += MAX_CONCURRENCY) {
        const batch = pageNumbers.slice(i, i + MAX_CONCURRENCY);
        const responses = await Promise.all(
          batch.map((p) => auditService.listAuditLog({ ...filters, page: p, page_size: EXPORT_PAGE_SIZE })),
        );
        for (const response of responses) rows.push(...(response.items || []));
      }
      const escape = (v: unknown): string => {
        if (v === null || v === undefined) return '';
        const s = typeof v === 'string' ? v : JSON.stringify(v);
        return '"' + s.replace(/"/g, '""').replace(/\r?\n/g, ' ') + '"';
      };
      const header = [
        'timestamp', 'actor_username', 'actor_ip', 'actor_roles', 'action', 'resource_type', 'resource_id',
        'http_method', 'http_path', 'status', 'old_value', 'new_value', 'error_message',
      ];
      const lines = [header.join(',')];
      for (const r of rows) {
        lines.push([
          escape(r.timestamp), escape(r.actor_username), escape(r.actor_ip), escape(r.actor_roles ? r.actor_roles.join('|') : ''),
          escape(r.action), escape(r.resource_type), escape(r.resource_id), escape(r.http_method), escape(r.http_path),
          escape(r.status), escape(r.old_value), escape(r.new_value), escape(r.error_message),
        ].join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFilename(filters);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[AUDIT LOG PAGE] CSV export failed:', err);
      setError(err instanceof Error ? err.message : 'CSV export failed');
    } finally {
      setLoading(false);
    }
  }, [canViewAuditLog, filters]);

  if (!canViewAuditLog) {
    return (
      <div className="page audit-log-page" data-testid="audit-access-denied">
        <div className="page-header">
          <div className="page-title">
            <h1>{t('audit.pageTitle')}</h1>
            <p>{t('audit.pageSubtitle')}</p>
          </div>
        </div>
        <div className="page-content">
          <div className="tpl-card tpl-state"><p>{t('audit.accessDenied')}</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page audit-log-page">
      <div className="page-header">
        <div className="page-title">
          <h1>{t('audit.pageTitle')}</h1>
          <p>{t('audit.pageSubtitle')}</p>
        </div>
        <div className="page-actions">
          <span className="last-updated">{t('common.updatedAt', { time: formatTime(lastUpdated) })}</span>
          <label className="tpl-check aud-auto" data-testid="audit-auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              aria-label={t('audit.autoRefresh')}
            />
            {t('audit.autoRefresh')}
          </label>
          <button type="button" className="refresh-button" onClick={handleRefresh} disabled={loading} data-testid="audit-refresh">
            {t('audit.refresh')}
          </button>
        </div>
      </div>

      <div className="dt-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'dashboard'}
          className={`dt-tab${activeTab === 'dashboard' ? ' active' : ''}`}
          onClick={() => setTab('dashboard')}
          data-testid="audit-tab-dashboard"
        >
          {t('audit.dashboard.tabDashboard')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'list'}
          className={`dt-tab${activeTab === 'list' ? ' active' : ''}`}
          onClick={() => setTab('list')}
          data-testid="audit-tab-list"
        >
          {t('audit.dashboard.tabList')}
        </button>
      </div>

      <div className="page-content audit-content">
        {error && <div className="error-message aud-error" role="alert">{error}</div>}

        {activeTab === 'dashboard' ? (
          <AuditDashboard
            refreshKey={lastUpdated.getTime()}
            onDrillDown={handleDrillDown}
          />
        ) : (
          <AuditLogTable
            items={items}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            filters={filters}
            onFilterChange={setFilters}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onExportCsv={handleExportCsv}
          />
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
